import styled from 'styled-components';
import { SxProps, Theme } from '@mui/material';
import { Flex, FlexColumn, Chip } from '@elementary/common-ui/components';
import {
  getAvatarColors,
  getFirstLettersOfName,
} from '@elementary/common-ui/utils/common';
import { useAuth } from '@frontegg/react';
import {
  fontSize,
  fontWeight,
  lineHeight,
  radius,
  space,
} from '@elementary/common-ui/style/utils';

const Circle = styled.div<{ $name: string; verified?: boolean }>`
  position: relative;
  min-width: 40px;
  min-height: 40px;
  border-radius: ${radius('round')};
  text-align: center;
  background: ${({ $name, verified, theme }) =>
    verified
      ? getAvatarColors($name, theme).background
      : theme.palette.surface.track};
  color: ${({ $name, verified, theme }) =>
    verified
      ? getAvatarColors($name, theme).text
      : theme.palette.content.inverse};
  border: ${({ verified, theme }) =>
    !verified && `2.5px dashed ${theme.palette.border.muted}`};
`;

const TextInCircle = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: inherit;
  font-size: ${fontSize('lg')};
`;

const Name = styled(Flex)`
  align-items: center;
  font-size: ${fontSize('base')};
  font-weight: ${fontWeight('medium')};
  line-height: ${lineHeight('sm')};
  color: ${({ theme }) => theme.palette.content.primary};
  gap: ${space('sm')};
`;

const NameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Email = styled.div`
  font-size: ${fontSize('md')};
  color: ${({ theme }) => theme.palette.content.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type Props = {
  id: string;
  name: string;
  email: string;
  verified?: boolean;
  sx?: SxProps<Theme>;
};

const UserCard = ({ id, name, email, verified = true, sx }: Props) => {
  const { user: currentUser } = useAuth();
  return (
    <Flex
      sx={[
        { alignItems: 'center', gap: 3, height: '48px' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Circle $name={id} verified={verified}>
        <TextInCircle>{getFirstLettersOfName(name ?? '')}</TextInCircle>
      </Circle>

      {name !== email ? (
        <FlexColumn sx={{ gap: 0.5, overflow: 'hidden' }}>
          <Name>
            <NameText>{name}</NameText>
            {currentUser?.email === email && (
              <Chip label="YOU" variant="outlined" size="medium" bold />
            )}
            {!verified && (
              <Chip
                label="Pending"
                variant="outlined"
                size="medium"
                customVariant="dashed"
                disabled
                bold
              />
            )}
          </Name>
          {verified && <Email>{email}</Email>}
        </FlexColumn>
      ) : (
        'Invite pending'
      )}
    </Flex>
  );
};

export default UserCard;
